import React, {
  memo,
  useState,
  useEffect,
  useRef,
  Children,
  cloneElement,
  ReactElement,
} from 'react'
import throttle from 'lodash/throttle'
import { makeStyles } from 'transfix-ui/core'
import { ITabProps } from './Tab'

const SCROLL_OFFSET = 48

export enum LAYOUT {
  fixed = 'tabs-fixed',
  scrollable = 'tabs-scrollable',
}

interface IChild extends ReactElement<ITabProps> {}

interface IIndicator {
  left: number
  width: number
}

export interface ITabsProps extends JSX.ElementChildrenAttribute {
  value: string
  onChange: (newTab: any) => any
  layout?: LAYOUT
  dataTest?: string
  displayIndicator?: boolean
  /**
   * Enable the possibility to add custom styles to tab implementations while keeping
   * them backward compatible with existing implementations. f.i: keep the tabList with scrollable styles
   * and fixed, having its label visible always (no ellipsis).
   */
  tabListClassName?: string
  tabPanelClassName?: string
  callToAction?: ReactElement
}

const isInHiddenModal = (node?: HTMLDivElement | null): boolean => {
  if (!node || !node.offsetParent) return true

  const { display, opacity } = global.getComputedStyle(node.offsetParent)
  return display === 'none' || opacity !== '1'
}

const useStyles = makeStyles(() => ({
  tabsContainer: {
    display: 'flex',
    justifyContent: 'space-between',
  },
  callToActionContainer: {
    display: 'flex',
    paddingRight: '14px',
    alignItems: 'center',
  },
}))

/**
 * @deprecated Use `transfix-ui/components/Tabs` instead, which is based on MUITabs component
 */
export const Tabs = memo<ITabsProps>(
  ({
    value,
    onChange,
    layout = LAYOUT.scrollable,
    children: childrenProp,
    dataTest,
    displayIndicator,
    tabListClassName = '',
    tabPanelClassName = '',
    callToAction,
  }) => {
    const classes = useStyles()
    const valueToIndex = new Map<string, number>()
    const valueToContent = new Map<string, {}>()
    const valueToErrorState = new Map<string, boolean>()
    const isScrollable = layout === LAYOUT.scrollable

    const tabClickHandler = (newTab: string) => {
      onChange(newTab)
    }

    let nameCounts = ''
    const tabs = (childrenProp as IChild[]).filter(Boolean)
    const children = Children.map(tabs, (tab, index) => {
      nameCounts += `${tab.props.label}:${tab.props.count},`
      const childValue = tab.props.value === undefined ? tab.props.label : tab.props.value
      const active = value === childValue
      valueToIndex.set(childValue, index + 1)
      valueToContent.set(childValue, tab.props.children)
      valueToErrorState.set(childValue, Boolean(tab.props.error))
      return cloneElement(tab, {
        value: childValue,
        active,
        onClick: tabClickHandler,
        dataTest: tab.props.dataTest || (dataTest ? `${dataTest}-tab-${index}` : undefined),
      })
    })

    const tabsContainer = useRef<HTMLDivElement>(null)

    // Implementing for now for the TMS usecase on the expandable row
    // Will dig in further for a better solution
    // For now this should not affect any other Tabs implementation

    const isTabIndicator = useRef(null)

    const [indicator, setIndicator] = useState<IIndicator>({ left: 0, width: 0 })
    const positionIndicator = (currentValue: string) => {
      let newValue
      if (tabsContainer.current) {
        const { left } = tabsContainer.current.getBoundingClientRect()
        const selectedTab = valueToIndex.get(currentValue)
        const tab =
          selectedTab && tabsContainer.current.querySelector(`.tab-item:nth-child(${selectedTab})`)
        if (tab) {
          const rect = tab.getBoundingClientRect()
          newValue = {
            left: rect.left - left + tabsContainer.current.scrollLeft,
            width: rect.width,
          }
          setIndicator(newValue)
        }
      }
      return newValue
    }

    const scrollIntoView = (indicatorPosition?: IIndicator) => {
      if (indicatorPosition && tabsContainer.current) {
        const { left, width } = indicatorPosition
        if (tabsContainer.current.clientWidth + tabsContainer.current.scrollLeft < left + width) {
          tabsContainer.current.scrollLeft =
            left + width + SCROLL_OFFSET - tabsContainer.current.clientWidth
        } else if (tabsContainer.current.scrollLeft > left) {
          tabsContainer.current.scrollLeft = left - SCROLL_OFFSET
        }
      }
    }

    const tabPanel = valueToContent.get(value)
    const errorTabSelected = valueToErrorState.get(value)

    useEffect(() => {
      scrollIntoView(positionIndicator(value))
      if (isInHiddenModal(tabsContainer.current)) {
        setTimeout(() => {
          scrollIntoView(positionIndicator(value))
        }, 500)
      }
      if (isScrollable) {
        const handler = throttle(() => {
          positionIndicator(value)
        }, 100)

        window.addEventListener('resize', handler)
        return () => {
          window.removeEventListener('resize', handler)
        }
      }
      return undefined
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [value, children.length, layout, nameCounts, tabPanel])

    return (
      <>
        <div
          ref={displayIndicator ? isTabIndicator : tabsContainer}
          className={`tabs-container ${layout} border-bottom-disabled ${tabListClassName}`}
          data-test={dataTest}
          role='tablist'>
          {callToAction ? (
            <div className={classes.tabsContainer}>
              <ul>{children}</ul>
              <div className={classes.callToActionContainer}>{callToAction}</div>
            </div>
          ) : (
            <ul>{children}</ul>
          )}
          <span
            className={`tabs-active-indicator ${errorTabSelected ? 'error' : ''}`}
            style={indicator}
            aria-hidden='true'
          />
        </div>
        {tabPanel && (
          <div
            role='tabpanel'
            data-test={dataTest ? `${dataTest}-panel` : undefined}
            className={tabPanelClassName}>
            {tabPanel}
          </div>
        )}
      </>
    )
  }
)

export default Tabs
